/**
 * QueryBuilder Store - React selector hooks.
 *
 * Derived state surfaces consumed by UI components. None of these mutate the
 * store — they read it and combine it with the script emitter to produce the
 * final Data(Q) text or look up the active entities.
 */
import { useMemo } from "react"
import ScriptGenerationService from "services/ScriptGenerationService"
import { EngineQueryResult } from "types/engineOutput.types"
import { BuilderTab, TransformPipeline } from "types/queryBuilder.types"
import { useQueryBuilderStore } from "./store"
import {
  buildBatchScript,
  buildFilterPrefix,
  buildTransformPrefix,
  builderToScriptConfig,
} from "./scriptEmission"

const EMPTY_RESULT: EngineQueryResult = {}

export const useActiveBuilder = (): BuilderTab | null => {
  const id = useQueryBuilderStore((s) => s.activeBuilderId)
  const builders = useQueryBuilderStore((s) => s.builders)
  return useMemo(
    () => builders.find((b) => b.id === id) ?? null,
    [id, builders]
  )
}

export const useActiveBuilderResult = (): EngineQueryResult => {
  const id = useQueryBuilderStore((s) => s.activeBuilderId)
  const lastId = useQueryBuilderStore((s) => s.lastRunBuilderId)
  const lastResult = useQueryBuilderStore((s) => s.lastRunResult)
  if (!id || id !== lastId) return EMPTY_RESULT
  return lastResult ?? EMPTY_RESULT
}

export const isBuilderRunnable = (
  b: Pick<BuilderTab, "rowVariables" | "columnVariables"> | null
): boolean =>
  !!b && b.rowVariables.length > 0 && b.columnVariables.length > 0

/**
 * Hook that returns the active builder's generated Data(Q) script, plus a
 * runnable flag.
 */
export const useBuilderScript = (): { script: string; runnable: boolean } => {
  const builder = useActiveBuilder()
  const pipelines = useQueryBuilderStore((s) => s.transformPipelines)

  return useMemo(() => {
    const runnable = isBuilderRunnable(builder)
    if (!runnable || !builder) return { script: "", runnable }

    // Per-builder pipeline selection. null/undefined or an unknown id ⇒
    // skip transforms entirely (raw data run).
    const pipelineId = builder.transformPipelineId ?? null
    const pipeline = pipelineId
      ? pipelines.find((p) => p.id === pipelineId)
      : null
    const transformPrefix = pipeline
      ? buildTransformPrefix(pipeline.steps)
      : ""
    const filterInfo = buildFilterPrefix(builder)

    const xtabFilter = filterInfo.filterFieldName
      ? filterInfo.filterFieldName
      : builder.universeFilter || undefined

    let script = ScriptGenerationService.generateComplexScript(
      builderToScriptConfig(builder, xtabFilter)
    )

    const prefixes: string[] = []
    if (transformPrefix) prefixes.push(transformPrefix)
    if (filterInfo.prefix) prefixes.push(filterInfo.prefix)
    if (prefixes.length > 0) {
      script = script.replace(/^(RESET\n)/, `$1${prefixes.join("\n")}\n`)
    }

    if (builder.isTransposed) {
      script = script.replace(/^OUTPUT (\S+)\s*$/m, "OUTPUT $1 /T")
    }

    return { script, runnable }
  }, [builder, pipelines])
}

/**
 * Hook that returns the DATA(Q) script for a "Run all" batch run: every
 * runnable builder concatenated into one engine query, with each distinct
 * Transform pipeline emitted at most once.
 *
 * `runnableCount` is the size of `includedBuilderIds` — the UI uses it to
 * label the button ("Run all (N)") and to disable it when there's nothing
 * to run.
 */
export const useBatchBuilderScript = (): {
  script: string
  includedBuilderIds: string[]
  runnableCount: number
} => {
  const builders = useQueryBuilderStore((s) => s.builders)
  const pipelines = useQueryBuilderStore((s) => s.transformPipelines)
  return useMemo(() => {
    const { script, includedBuilderIds } = buildBatchScript(builders, pipelines)
    return {
      script,
      includedBuilderIds,
      runnableCount: includedBuilderIds.length,
    }
  }, [builders, pipelines])
}

/** Result of the most recent batch "Run all" run plus the ordered list of
 *  builder ids that contributed an OUTPUT line. Returns `null` when no
 *  batch has run yet. */
export const useBatchRunResult = (): {
  result: EngineQueryResult | null
  builderIds: string[]
} => {
  const result = useQueryBuilderStore((s) => s.batchRunResult)
  const builderIds = useQueryBuilderStore((s) => s.batchRunBuilderIds)
  return { result, builderIds }
}

/** Active transform pipeline (the one currently being edited in Transform). */
export const useActiveTransformPipeline = (): TransformPipeline | null => {
  const pipelines = useQueryBuilderStore((s) => s.transformPipelines)
  const activeId = useQueryBuilderStore((s) => s.activeTransformPipelineId)
  return useMemo(
    () => pipelines.find((p) => p.id === activeId) ?? null,
    [pipelines, activeId]
  )
}

/** Steps in the active transform pipeline (or [] if none). */
export const useActiveTransformSteps = () => {
  const pipeline = useActiveTransformPipeline()
  return pipeline?.steps ?? []
}
